// v3 fence, authored the way age-of-mcp writes it: light blond-timber
// post-and-rail panel whose broad flat top cap reads as a crisp pale line from
// above. The library has no terrain and no neighbour graph, so the panel is
// rendered standalone (connections = 0, flat heights) — exactly the look the
// flattened source baked: a capped post per build stage and a staked-out
// groundwork board at stage 0. Ported 1:1 from age-of-mcp's v3/fence.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Light blond sanded timber — the v3 palette for this fence.
const POST_COLOR = "#9c8154";   // posts, a shade deeper than the rails
const RAIL_COLOR = "#cdb684";   // pale sanded boards
const CAP_COLOR  = "#e6d6ac";   // bright post caps, readable from above
const STAKE_GROUND = "#8a8170"; // stage-0 layout board

// The eight neighbour directions in connection-mask bit order. Local +X is
// east, local +Z is north — matching how the renderer computes the mask.
const DIRS8: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];

const STAGE_H = [0, 0.18, 0.30, 0.42];
const POST_W  = 0.08;
const TOP_RAIL_H = 0.035;   // top rail: thin in height…
const TOP_RAIL_W = 0.095;   // …but broad in plan — the line the camera sees
const MID_RAIL_S = 0.04;    // square mid rail

// A post with a bright flat cap board on top.
function cappedPost(x: number, y: number, z: number, postH: number, w: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(w, postH, w),
      color: POST_COLOR, position: [x, y + postH / 2, z] },
    { geometry: new THREE.BoxGeometry(w + 0.04, 0.022, w + 0.04),
      color: CAP_COLOR, position: [x, y + postH + 0.011, z] },
  ];
}

// Stage-0 groundwork: a staked-out layout board.
function groundwork(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.6, 0.035, 0.6),
      color: STAKE_GROUND, position: [0, 0.0175, 0] },
  ];
  for (const [sx, sz] of [[-0.22, 0.22], [0.22, -0.22]] as const) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.04, 0.12, 0.04),
      color: POST_COLOR, position: [sx, 0.06, sz],
    });
  }
  return parts;
}

/**
 * One half-arm per connected direction — from this post to the shared
 * boundary point. Standalone (connections = 0) draws nothing here; kept verbatim
 * from the source so the neighbour-aware logic is preserved for reference.
 */
function connectionArms(h: number, connections: number, heights: readonly number[]): Part[] {
  const has = (i: number): boolean => (connections & (1 << i)) !== 0;
  const parts: Part[] = [];
  const postH = h + 0.05;

  for (let i = 0; i < 8; i++) {
    if (!has(i)) continue;
    const diagonal = (i & 1) === 1;
    if (diagonal && (has((i + 7) % 8) || has((i + 1) % 8))) continue;

    const [dx, dz] = DIRS8[i]!;
    const horiz = diagonal ? Math.SQRT1_2 : 0.5;   // run to the boundary point
    const dY    = heights[i] ?? 0;                 // rise to the boundary point
    const pitch = Math.atan2(dY, horiz);
    const len3D = Math.sqrt(horiz * horiz + dY * dY);
    const yaw   = Math.atan2(-dz, dx);
    const rot: [number, number, number] = [0, yaw, pitch];

    parts.push({
      geometry: new THREE.BoxGeometry(len3D, TOP_RAIL_H, TOP_RAIL_W),
      color: RAIL_COLOR,
      position: [dx * 0.25, h + dY * 0.5, dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });
    parts.push({
      geometry: new THREE.BoxGeometry(len3D, MID_RAIL_S, MID_RAIL_S),
      color: RAIL_COLOR,
      position: [dx * 0.25, h * 0.52 + dY * 0.5, dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });

    if (i < 4) {
      parts.push(...cappedPost(dx * 0.5, dY, dz * 0.5, postH, 0.07));
    }
  }
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return groundwork();
  const h = STAGE_H[Math.min(s, 3)]!;
  if (h <= 0) return [];
  return [
    ...cappedPost(0, 0, 0, h + 0.05, POST_W),
    ...connectionArms(h, 0, []),
  ];
}

export const fenceV3 = defineModel({
  id: "fence-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Fence (v3)",
  tags: ["building","fence","low-poly","age-of-mcp","v3"],
  summary: "Low-poly fence.",
  description: "A low wooden fence panel that blocks passage.",
  author: "age-of-mcp",
  footprint: 0.8,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
